import { apiFetch } from "./client";

/**
 * 3B «Запит у відділ» — department-queue internal requests API.
 * requestType → department (queue); assignedUser=NULL until claim/assign.
 */

export type RequestType =
  | "reconciliation_statement" | "payment_schedule" | "contract_review_or_addendum"
  | "pos_materials" | "data_correction" | "complaint_support" | "other"
  // #9 canonical catalog (S2, D-040 — served when the backend flag is ON)
  | "payment_check" | "contract_review" | "contract_addendum" | "contract_terms_change" | "promo" | "custom_request";

export type RequestStatus = "new" | "assigned" | "in_progress" | "needs_info" | "done" | "rejected" | "cancelled";

export const REQUEST_TYPE_LABEL: Record<RequestType, string> = {
  reconciliation_statement: "Акт звірки",
  payment_schedule: "Графік погашення",
  contract_review_or_addendum: "Перегляд договору / допугода",
  pos_materials: "POS / вивіска",
  data_correction: "Виправлення даних 1С",
  complaint_support: "Рекламація / якість",
  other: "Інше / нестандартний запит",
  // canonical (label normally comes from the backend route row)
  payment_check: "Перевірити оплату",
  contract_review: "Перевірити договір",
  contract_addendum: "Додаткова угода",
  contract_terms_change: "Зміна умов договору",
  promo: "Акція / промо",
  custom_request: "Інше",
};

export const REQUEST_STATUS_LABEL: Record<RequestStatus, string> = {
  new: "Новий", assigned: "Призначено", in_progress: "В роботі",
  needs_info: "Очікує уточнення",
  done: "Виконано", rejected: "Відхилено", cancelled: "Скасовано",
};

export interface RequestTypeOption {
  type: RequestType;
  /** #9 canonical: backend-owned UA label (route row). Present ⇒ canonical catalog. */
  label?: string;
  departmentId: string | null;
  departmentName: string | null;
  defaultDueDays: number | null;
  /** #9 canonical SLA preview (calendar hours, P8). */
  claimSlaHours?: number | null;
  completeSlaHours?: number | null;
  slaNote?: string;
  available: boolean;
  reason: string | null;
}
export interface RequestTypesResponse {
  types: RequestTypeOption[];
  departments: { id: string; name: string }[];
}

export interface RequestRow {
  id: string;
  subject: string;
  requestType: RequestType | string;
  departmentName: string | null;
  status: RequestStatus;
  dueDate: string | null;
  overdue: boolean;
  result: string | null;
  assigneeName: string | null;
  holdingName: string | null;
  requesterName?: string | null;
  canClaim?: boolean;
  canAssign?: boolean;
  // #9 S2 additive (present when serving the canonical module)
  requestNumber?: string | null;
  resultStatus?: string | null;
  slaStatus?: string | null;
  priority?: "Normal" | "High";
  claimDueAt?: string | null;
  completeDueAt?: string | null;
  slaOverdue?: boolean;
  /** inbox rows only (canonical) — для бакета «Призначені мені». */
  assignedUserId?: string | null;
}

export interface CreateRequestPayload {
  requestType: RequestType;
  subject: string;
  body?: string;
  departmentId?: string;     // required for 'other'/'custom_request'
  dueDate?: string;          // auto from route SLA / defaultDueDays if omitted
  priority?: "Normal" | "High";
  holdingId?: string;
  accountId?: string;
  outletId?: string;
  contactId?: string;
  contractId?: string;
  // #9 S2 (canonical module)
  desiredDueDate?: string;
  formData?: Record<string, string>;
  attachmentFileIds?: string[];
  sourceModule?: string;
  sourceEntityId?: string;
  sourceReasonCode?: string;
  complaintId?: string;
}

// ── #9 S2 — Request Detail ──────────────────────────────────────────────────

export interface RequestComment {
  id: string;
  authorId: string;
  authorName: string | null;
  body: string;
  kind: "comment" | "needs_info" | "answer" | "result";
  createdAt: string;
}
export interface RequestHistoryItem {
  fromStatus: string | null;
  toStatus: string;
  actorName: string | null;
  note: string | null;
  at: string;
}
export interface RequestAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  uploadedByName: string | null;
  createdAt: string;
}
export interface RequestAllowedActions {
  canClaim: boolean;
  canAssign: boolean;
  canStart: boolean;
  canComplete: boolean;
  canReject: boolean;
  canRequestInfo: boolean;
  canAnswer: boolean;
  canCancel: boolean;
  canComment: boolean;
  canAttach: boolean;
}
export interface RequestDetailResponse {
  request: {
    id: string;
    requestNumber: string | null;
    requestType: string;
    subject: string;
    body: string | null;
    status: RequestStatus;
    priority: "Normal" | "High";
    dueDate: string | null;
    desiredDueDate: string | null;
    claimDueAt: string | null;
    completeDueAt: string | null;
    slaStatus: string | null;
    result: string | null;
    resultStatus: string | null;
    rejectReason: string | null;
    formData: Record<string, string> | null;
    departmentId: string | null;
    departmentName: string | null;
    requesterId: string;
    requesterName: string | null;
    assignedUserId: string | null;
    assigneeName: string | null;
    createdAt: string | null;
    sourceModule: string | null;
  };
  context: { requestId: string; holdingId: string | null; holdingName: string | null; scope: string };
  comments: RequestComment[];
  history: RequestHistoryItem[];
  attachments: RequestAttachment[];
  allowedActions: RequestAllowedActions;
}

export function fetchRequestTypes(signal?: AbortSignal): Promise<RequestTypesResponse> {
  return apiFetch("/Custom/frontend/request-types", { signal });
}
export function fetchMyRequests(signal?: AbortSignal): Promise<{ items: RequestRow[] }> {
  return apiFetch("/Custom/frontend/internal-requests/mine", { signal });
}
export interface InboxDepartment {
  id: string;
  name: string;
  isHead: boolean;
  responsibles: { id: string; name: string }[];
  items: RequestRow[];
}
export function fetchInbox(signal?: AbortSignal): Promise<{ departments: InboxDepartment[] }> {
  return apiFetch("/Custom/frontend/internal-requests/inbox", { signal });
}
export function createRequest(body: CreateRequestPayload): Promise<{ ok: boolean; id: string; departmentName: string; dueDate: string }> {
  return apiFetch("/Custom/frontend/internal-requests", { method: "POST", body });
}
export function claimRequest(id: string): Promise<{ ok: boolean; status: string }> {
  return apiFetch(`/Custom/frontend/internal-requests/${encodeURIComponent(id)}/claim`, { method: "POST" });
}
export function assignRequest(id: string, userId: string): Promise<{ ok: boolean; status: string }> {
  return apiFetch(`/Custom/frontend/internal-requests/${encodeURIComponent(id)}/assign`, { method: "POST", body: { userId } });
}
export function patchRequest(
  id: string,
  body: {
    status: RequestStatus; result?: string; rejectReason?: string;
    /** #9: done-gate P3 */
    resultStatus?: string;
    followUpTask?: { title?: string; dueDate?: string };
    /** #9 S4: needs_info-gate */
    question?: string;
  },
): Promise<{ ok: boolean; status: string; followUpTaskId?: string }> {
  return apiFetch(`/Custom/frontend/internal-requests/${encodeURIComponent(id)}`, { method: "PATCH", body });
}
/** #9 S4 — thread comment (participant). */
export function postRequestComment(id: string, body: string): Promise<{ ok: boolean; id: string | null }> {
  return apiFetch(`/Custom/frontend/internal-requests/${encodeURIComponent(id)}/comments`, { method: "POST", body: { body } });
}
/** #9 S4 — requester answers needs_info (SLA resume, → in_progress). */
export function answerRequest(id: string, body: string): Promise<{ ok: boolean; status: string }> {
  return apiFetch(`/Custom/frontend/internal-requests/${encodeURIComponent(id)}/answer`, { method: "POST", body: { body } });
}
export function fetchRequestDetail(id: string, signal?: AbortSignal): Promise<RequestDetailResponse> {
  return apiFetch(`/Custom/frontend/internal-requests/${encodeURIComponent(id)}`, { signal });
}
export function bindRequestAttachment(id: string, fileId: string): Promise<{ ok: boolean; id: string; fileName: string; fileSize: number }> {
  return apiFetch(`/Custom/frontend/internal-requests/${encodeURIComponent(id)}/attachments`, { method: "POST", body: { fileId } });
}
/** Secured streaming download URL (custom endpoint — never the native /Attachment/file). */
export function requestAttachmentDownloadUrl(requestId: string, attachmentId: string): string {
  return `/api/v1/Custom/frontend/internal-requests/${encodeURIComponent(requestId)}/attachments/${encodeURIComponent(attachmentId)}/download`;
}

// #9 S5 Admin config — АБСОРБОВАНО у Admin Center #6 (D-044 S3, AC-ADM-050):
// див. api/adminCenter.ts (fetchRoutesConfig / routesMutations).
